import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { categories, products, purchaseSteps, whyBuy } from "@/data/products";
import useHashScroll from "@/lib/useHashScroll";
import { scrollToHash } from "@/lib/scroll";
import Seo from "@/components/site/Seo";
import { EASE, Eyebrow, Reveal, fadeUp, staggerContainer } from "@/components/site/motion";

function CatalogueVisual() {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-white/85 shadow-[0_40px_80px_-45px_rgba(11,18,32,0.35)] backdrop-blur">
      <div className="bg-grid-light absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="relative flex items-center gap-2 border-b border-slate-100 px-5 py-3.5">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
          bytevora.catalogue
        </span>
      </div>
      <svg viewBox="0 0 480 320" className="relative block h-auto w-full" role="img" aria-label="Product catalogue diagram">
        <defs>
          <linearGradient id="cat-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
        </defs>
        <rect x="180" y="26" width="120" height="36" rx="10" fill="url(#cat-grad)" />
        <text x="240" y="48" fontSize="10" fill="#fff" fontFamily="'Geist Mono', monospace" letterSpacing="2" textAnchor="middle">PRODUCTS</text>
        {[[96, 130], [240, 130], [384, 130]].map(([x, y], i) => (
          <line key={i} x1="240" y1="62" x2={x} y2={y} stroke="url(#cat-grad)" strokeWidth="1.4" opacity="0.55" className={i === 1 ? "dash-flow" : ""} />
        ))}
        {[["audits", 96], ["packages", 240], ["consulting", 384]].map(([l, x]) => (
          <g key={l}>
            <rect x={x - 62} y="130" width="124" height="34" rx="9" fill="#fff" stroke="url(#cat-grad)" strokeWidth="1.5" />
            <text x={x} y="151" fontSize="10" fill="#64748B" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">{l}</text>
          </g>
        ))}
        {[[96, 130, ["review", "audit"]], [240, 130, ["cloud", "website"]], [384, 130, ["session"]]].map(([x, , leaves]) =>
          leaves.map((leaf, li) => (
            <g key={leaf}>
              <line x1={x} y1="164" x2={x + (leaves.length > 1 ? (li === 0 ? -34 : 34) : 0)} y2={216} stroke="url(#cat-grad)" strokeWidth="1.1" opacity="0.4" />
              <rect x={x + (leaves.length > 1 ? (li === 0 ? -34 : 34) : 0) - 44} y="216" width="88" height="30" rx="8" fill="#F8FAFC" stroke="url(#cat-grad)" strokeWidth="1.1" />
              <text x={x + (leaves.length > 1 ? (li === 0 ? -34 : 34) : 0)} y="235" fontSize="9" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">{leaf}</text>
            </g>
          ))
        )}
        <circle cx="240" cy="290" r="5" fill="none" stroke="url(#cat-grad)" strokeWidth="1.5" className="node-pulse" />
        <circle cx="240" cy="290" r="4" fill="url(#cat-grad)" />
        <text x="240" y="282" fontSize="9" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1.5" textAnchor="middle">digital delivery</text>
      </svg>
    </div>
  );
}

function ProductCard({ p, i }) {
  return (
    <motion.div variants={fadeUp} custom={i}>
      <Link
        to={`/products/${p.slug}`}
        data-testid={`product-card-${p.slug}`}
        className="group flex h-full flex-col rounded-[18px] border border-slate-200 bg-white p-7 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-blue-600/40 hover:shadow-[0_28px_55px_-32px_rgba(37,99,235,0.4)]"
      >
        <div className="flex items-start justify-between">
          <span className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 bg-slate-50 text-blue-600">
            <p.icon size={19} strokeWidth={1.7} />
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{p.category}</span>
        </div>
        <h3 className="mt-6 text-lg font-semibold tracking-tight text-slate-900">{p.name}</h3>
        <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{p.cardDesc}</p>
        <div className="mt-6 border-t border-slate-100 pt-5">
          <p className="text-[26px] font-bold tracking-tight text-slate-900">{p.priceLabel}</p>
          <p className="mt-1.5 text-xs text-slate-500">
            Delivery: {p.delivery} · {p.deliveryType}
          </p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 transition-colors duration-300 group-hover:text-blue-600">
            {p.cardCta}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

function CategoryBlock({ title, eyebrow, heading, copy, items, anchorId, flipBg }) {
  const list = products.filter((p) => p.category === title);
  return (
    <section id={anchorId} className={`scroll-mt-28 py-24 md:py-28 ${flipBg ? "bg-white" : "bg-[#F8FAFC]"}`} data-testid={`category-${anchorId}`}>
      <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
        <Reveal className="max-w-2xl">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
            {heading}
          </h2>
          <p className="mt-5 text-[15px] leading-[1.8] text-slate-600 md:text-base">{copy}</p>
        </Reveal>
        <div className="mt-14 flex flex-col gap-5">
          {list.map((p, i) => (
            <Reveal key={p.slug} delay={Math.min(i, 1)}>
              <div className="grid gap-8 rounded-[20px] border border-slate-200 bg-white p-8 md:p-10 lg:grid-cols-[1.2fr_0.8fr] lg:gap-14">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-blue-600">{p.positioning}</p>
                  <h3 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">{p.name}</h3>
                  <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-[15px]">{p.sub}</p>
                  {(p.included.length > 0 || p.topics) && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      {(p.topics || p.included).slice(0, 6).map((c) => (
                        <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12.5px] font-medium text-slate-600">
                          <Check size={12} className="text-blue-600" strokeWidth={2.5} />
                          {c}
                        </span>
                      ))}
                      {(p.topics || p.included).length > 6 && (
                        <span className="rounded-full px-3 py-1.5 text-[12.5px] font-medium text-slate-400">
                          +{(p.topics || p.included).length - 6} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-slate-50/70 p-7">
                  <div>
                    <p className="text-[30px] font-bold tracking-tight text-slate-900">{p.priceLabel}</p>
                    <p className="mt-2 text-xs leading-relaxed text-slate-500">
                      {p.delivery} · {p.deliveryType}
                    </p>
                    {p.scopeNote && (
                      <p className="mt-4 border-l-2 border-blue-600/40 pl-3.5 text-xs leading-relaxed text-slate-500">
                        {p.scopeNote}
                      </p>
                    )}
                  </div>
                  <Link
                    to={`/products/${p.slug}`}
                    data-testid={`category-cta-${p.slug}`}
                    className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
                  >
                    {p.cardCta}
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ProductsPage() {
  useHashScroll();

  return (
    <main className="bg-white" data-testid="products-page">
      <Seo
        title="Digital Products & Technology Packages | Bytevora"
        description="Explore Bytevora digital products, technical audits, cloud deployment packages, business website packages, and technology consultation services with clear pricing and digital delivery."
        path="/products"
      />

      {/* Hero */}
      <section className="relative overflow-hidden pt-[72px]">
        <div className="bg-grid-light absolute inset-0" aria-hidden="true" />
        <div className="absolute -right-40 top-[-120px] h-[420px] w-[420px] rounded-full bg-blue-600/10 blur-[120px]" aria-hidden="true" />
        <div className="relative mx-auto grid w-full max-w-[1200px] items-center gap-14 px-6 py-20 md:px-8 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
              <Eyebrow>Products</Eyebrow>
            </motion.div>
            <motion.h1
              data-testid="products-hero-heading"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.95, ease: EASE, delay: 0.12 }}
              className="mt-6 text-[clamp(2.5rem,5vw,4.2rem)] font-extrabold leading-[1.03] tracking-[-0.03em] text-slate-900"
            >
              Technology products built for{" "}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                practical use.
              </span>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.28 }}
            >
              <p className="mt-6 max-w-[560px] text-base leading-relaxed text-slate-600 md:text-lg">
                Explore defined digital products, implementation packages, technical reviews, and
                professional technology services from Bytevora — with clear scope, pricing, and
                digital delivery.
              </p>
              <p className="mt-4 max-w-[560px] text-sm leading-relaxed text-slate-500">
                Built for businesses, founders, and teams that need a clear technical outcome
                without starting with a fully custom engagement.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  data-testid="products-explore-button"
                  onClick={() => scrollToHash("#catalogue")}
                  className="group inline-flex items-center gap-2.5 rounded-[10px] bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
                >
                  Explore Products
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <Link
                  to="/contact"
                  data-testid="products-custom-link"
                  className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
                >
                  Need Something Custom?
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.35 }}
            className="hidden lg:col-span-5 lg:block"
          >
            <CatalogueVisual />
          </motion.div>
        </div>
      </section>

      {/* Category navigation */}
      <div className="sticky top-[72px] z-40 border-y border-slate-200 bg-white/90 backdrop-blur-xl" data-testid="products-category-nav">
        <div className="mx-auto flex w-full max-w-[1200px] gap-2 overflow-x-auto px-6 py-3.5 md:px-8">
          {[
            { label: "All Products", hash: "#catalogue" },
            { label: "Audits & Reviews", hash: "#audits" },
            { label: "Implementation Packages", hash: "#packages" },
            { label: "Consulting", hash: "#consulting" },
          ].map((c) => (
            <button
              key={c.label}
              type="button"
              data-testid={`cat-tab-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => scrollToHash(c.hash)}
              className="whitespace-nowrap rounded-full border border-slate-200 bg-white px-4.5 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-600 transition-[border-color,color] duration-200 hover:border-blue-600/50 hover:text-blue-600"
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Featured products */}
      <section id="catalogue" className="scroll-mt-32 bg-white py-24 md:py-28" data-testid="products-featured">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Available products</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.5rem]">
              Defined scope. Clear pricing. Digital delivery.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Each product is designed around a specific outcome, with transparent inclusions,
              delivery expectations, and pricing before purchase.
            </p>
          </Reveal>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3"
          >
            {products.map((p, i) => (
              <ProductCard key={p.slug} p={p} i={i} />
            ))}
          </motion.div>
        </div>
      </section>

      <CategoryBlock
        title="Audits & Reviews"
        anchorId="audits"
        eyebrow="Audits & Reviews"
        heading="Understand what should be improved before deciding what to build."
        copy="Bytevora's audit and review products provide structured technical findings and practical recommendations before you commit to larger implementation work."
        flipBg={false}
      />
      <CategoryBlock
        title="Implementation Packages"
        anchorId="packages"
        eyebrow="Implementation Packages"
        heading="Defined technical implementation without an open-ended project scope."
        copy="These packages are designed for customers who need a specific technical outcome with a defined base scope, delivery process, and price."
        flipBg={true}
      />
      <CategoryBlock
        title="Consulting"
        anchorId="consulting"
        eyebrow="Consulting"
        heading="Make the technical decision before making the expensive one."
        copy="Get focused technical guidance before committing to architecture, infrastructure, development, automation, AI, or product decisions."
        flipBg={false}
      />

      {/* How purchasing works */}
      <section className="bg-white py-24 md:py-28" data-testid="products-how-it-works">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-xl">
            <Eyebrow>How it works</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              A clear process from purchase to delivery.
            </h2>
          </Reveal>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="relative mt-16 grid gap-12 md:grid-cols-4 md:gap-8"
          >
            <div className="absolute left-0 right-0 top-6 hidden border-t border-dashed border-slate-200 md:block" aria-hidden="true" />
            {purchaseSteps.map((s) => (
              <motion.div key={s.num} variants={fadeUp} data-testid={`purchase-step-${s.num}`} className="group relative">
                <div className="relative z-10 grid h-12 w-12 place-items-center rounded-full border border-slate-200 bg-white font-mono text-[13px] tracking-[0.1em] text-slate-500 transition-[border-color,color,box-shadow] duration-300 group-hover:border-blue-600/50 group-hover:text-blue-600 group-hover:shadow-[0_0_0_6px_rgba(37,99,235,0.08)]">
                  {s.num}
                </div>
                <h3 className="mt-6 text-lg font-semibold tracking-tight text-slate-900">{s.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why buy */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="products-why">
        <div className="mx-auto grid w-full max-w-[1200px] gap-12 px-6 md:px-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Why Bytevora</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              Clear scope before you pay.
            </h2>
          </Reveal>
          <div className="grid gap-8 sm:grid-cols-2 lg:col-span-7">
            {whyBuy.map((p, i) => (
              <Reveal key={p.title} delay={i}>
                <span className="grid h-8 w-8 place-items-center rounded-full bg-blue-600/10 text-blue-600">
                  <Check size={15} strokeWidth={2.5} />
                </span>
                <h3 className="mt-4 text-[16px] font-semibold tracking-tight text-slate-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Custom CTA */}
      <section className="relative overflow-hidden bg-navy py-24 md:py-28" data-testid="products-custom-cta">
        <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
        <div className="absolute -left-40 bottom-[-140px] h-[420px] w-[420px] rounded-full bg-blue-600/25 blur-[130px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-[860px] px-6 text-center md:px-8">
          <Reveal>
            <h2 className="text-3xl font-extrabold leading-[1.06] tracking-[-0.02em] text-white sm:text-4xl md:text-[2.6rem]">
              Need something beyond{" "}
              <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                a fixed product?
              </span>
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-slate-400 md:text-base">
              Some technology requirements do not fit neatly into a standard package. If you need
              custom software, a larger cloud environment, advanced integrations, AI implementation,
              or a more complex digital product, talk to Bytevora about a custom engagement.
            </p>
          </Reveal>
          <Reveal delay={2} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="products-custom-start-project"
              className="group inline-flex items-center gap-2.5 rounded-[10px] bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-blue-600 hover:text-white"
            >
              Start a Project
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="products-custom-services"
              className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:text-blue-300"
            >
              Explore Services
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Legal / delivery note */}
      <section className="bg-white py-16 md:py-20" data-testid="products-legal-note">
        <div className="mx-auto max-w-[860px] px-6 md:px-8">
          <Reveal>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-7 md:p-8">
              <p className="font-mono text-[10.5px] uppercase tracking-[0.24em] text-slate-400">Purchase note</p>
              <p className="mt-4 text-sm leading-[1.8] text-slate-600">
                All Bytevora products are delivered digitally — no physical shipping applies.
                Applicable taxes, if any, will be shown or communicated before payment. By
                completing a purchase, you acknowledge the applicable product scope and agree to
                Bytevora&rsquo;s{" "}
                <Link to="/terms-and-conditions" className="font-medium text-blue-600 underline decoration-blue-600/30 underline-offset-4 hover:decoration-blue-600">Terms &amp; Conditions</Link>,{" "}
                <Link to="/refund-cancellation-policy" className="font-medium text-blue-600 underline decoration-blue-600/30 underline-offset-4 hover:decoration-blue-600">Refund &amp; Cancellation Policy</Link>, and{" "}
                <Link to="/shipping-delivery-policy" className="font-medium text-blue-600 underline decoration-blue-600/30 underline-offset-4 hover:decoration-blue-600">Shipping &amp; Delivery Policy</Link>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
